import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
};

const variants = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800'
};

export const ToastContainer: React.FC = () => {
  const { notifications, removeNotification } = useAppStore();

  // Auto-hide notifications based on type
  useEffect(() => {
    notifications.forEach(notification => {
      if (!notification.read) {
        let timeout: number;
        
        if (notification.type === 'info' || 
            (notification.type === 'success' && notification.priority === 'low')) {
          // Auto-hide info and low-priority success notifications after 1.5 seconds
          timeout = window.setTimeout(() => {
            removeNotification(notification.id);
          }, 1500);
        } else if (notification.type === 'success') {
          // Auto-hide other success notifications after 3 seconds
          timeout = window.setTimeout(() => {
            removeNotification(notification.id);
          }, 3000);
        }
        
        return () => {
          if (timeout) clearTimeout(timeout);
        };
      }
    });
  }, [notifications, removeNotification]);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {notifications.map((notification) => {
          const Icon = icons[notification.type];
          const shouldShowCloseButton = notification.type === 'error' || 
                                       notification.type === 'warning' || 
                                       (notification.type === 'success' && notification.priority !== 'low');
          
          return (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              className={`
                flex items-start p-4 rounded-lg border shadow-lg min-w-80 max-w-md
                ${variants[notification.type]}
              `}
            >
              <Icon className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              
              <div className="flex-1">
                <h4 className="font-medium">{notification.title}</h4>
                <p className="text-sm mt-1 opacity-90">{notification.message}</p>
              </div>
              
              {shouldShowCloseButton && (
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="ml-3 p-1 hover:bg-black/5 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};